import { Component, OnInit } from '@angular/core';
import { trigger, transition, style, animate, query, group } from '@angular/animations';
import { UntypedFormGroup, Validators, UntypedFormControl } from '@angular/forms';
import { Router } from '@angular/router';

import swal from 'sweetalert2';

import { AuthService } from '../auth.service';
import { StoreHelper } from '../../shared/helpers/store.helper';
import { User } from '../../shared/models/user';
import { matchOtherValidator } from './1-match-other-validator';
import { ControllerService } from '../../shared/services/controller.service';
import { googleAnalyticsEvent } from '../../shared/helpers/google-analytics-event';

@Component({
  selector: 'app-signup',
  templateUrl: './signin.component.html',
  styleUrls: ['./signin.component.scss'],
  standalone: false,
  animations: [
    trigger('slideSwitcher', [
      transition('1 => 2', [
        group([
          query(':enter', [
            style({ transform: 'translateX(100%)', position: 'absolute', top: 0, left: 0, width: '100%' }),
            animate('0.5s ease-out', style({ transform: 'translateX(0%)' }))
          ], { optional: true }),
          query(':leave', [
            style({ transform: 'translateX(0%)' }),
            animate('0.5s ease-out', style({ transform: 'translateX(-100%)' }))
          ], { optional: true })
        ])
      ]),
      transition('2 => 1', [
        group([
          query(':enter', [
            style({ transform: 'translateX(-100%)', position: 'absolute', top: 0, left: 0, width: '100%' }),
            animate('0.5s ease-out', style({ transform: 'translateX(0%)' }))
          ], { optional: true }),
          query(':leave', [
            style({ transform: 'translateX(0%)' }),
            animate('0.5s ease-out', style({ transform: 'translateX(100%)' }))
          ], { optional: true })
        ])
      ])
    ])
  ]
})
export class SigninComponent implements OnInit {
  signinForm: UntypedFormGroup;
  user: User;
  currentIndex: number = 1;
  inputEnabled = true;
  privacyPolicy = false;
  maxIndex: number = 5;
  minIndex: number = 1;
  loginError = ''
  showPassword = false;

  constructor(private authService: AuthService, private router: Router, private controller: ControllerService) {

  }

  ngOnInit() {
    this.controller.shiftContainerToMiddle()

    document.body.classList.remove('background_v2')

    this.signinForm = new UntypedFormGroup({
      'userData': new UntypedFormGroup(
        {
          'email': new UntypedFormControl('', [Validators.required, emailValidator]),
          'password': new UntypedFormControl(null, [Validators.required]),
          'passwordConfirm': new UntypedFormControl(null, [Validators.required, matchOtherValidator('password')]),
          'invitationCode': new UntypedFormControl(null, [Validators.required]),
          'privacyPolicy': new UntypedFormControl(null, [Validators.requiredTrue])
        },
        passwordMatchValidator
      )
    });
    function emailValidator(control: any) {
      // RFC 2822 compliant regex
      if (control.value != null && control.value.match(/[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?/)) {
        return null;
      } else {
        return { 'invalidEmailAddress': true };
      }
    }
    function passwordMatchValidator(g: any) {
      return g.get('password').value === g.get('passwordConfirm').value ? null : { 'mismatch': true };
    }
  }

  onBack() {
    this.currentIndex = 1;
    this.inputEnabled = true;
  }

  onNext() {
    this.currentIndex = 2;
    this.inputEnabled = true;

    // Focus password input on next tick to ensure visibility
    setTimeout(() => {
        const passwordInput = document.getElementById('password');
        if (passwordInput) passwordInput.focus();
    }, 501);
  }

  async checkIfExisting(input: any) {

    if (!input.valid) {
      input.markAsTouched();
      this.inputEnabled = true
      return false
    }

    try {
      const existing = await this.authService.existingEmail(input.value.trim())

      if (existing) {
        this.onNext();
        return
      }

      this.router.navigate(['/signup'], { queryParams: { email: input.value } })

    } catch (e) {
      swal({
        title: 'There is a problem',
        text: 'Error connecting to server.',
        showCloseButton: true,
        customClass: 'custom-swal2-hook'
      });
    }
  }

  onConfirm(input: any) {
    if (input.valid) {
      this.onNext();
    } else {
      this.inputEnabled = true;
      return false;
    }
  }

  async signIn(email, password) {
    if (!this.inputEnabled) {
      return
    }
    this.inputEnabled = false;

    try {
      const user: any = await this.authService.signin(email.value.trim(), password.value)
      if (!user.success) {
        if (user.message == 'out_of_contract') {
          this.loginError = 'Your access to Hemisphere has expired or is no longer active. Please contact your Rare Client Success Manager for support. '
        } else {
          this.loginError = 'Please check your email address or reset your password by clicking on "Forgot your password?'
        }
        throw new Error("Login Failed")
      }
      this.controller.removeCookie('alreadySentFeedback')
      this.controller.removeCookie('anonymousOneTimeSubmit')
      const currentUser = StoreHelper.getCurrentUser();
      const GAtype = currentUser.kind;
      googleAnalyticsEvent('Sign in', 'Sign in complete - ' + GAtype);

      this.router.navigate(['/start']);
    }
    catch (e) {
      swal({
        title: 'Invalid username/password',
        text: this.loginError,
        showCancelButton: false,
        showConfirmButton: true,
        showCloseButton: true,
        customClass: 'custom-swal2-hook'
      });
      this.inputEnabled = true
    }

  }

  onPasswordEnter(event) {
    event.preventDefault();
    event.stopPropagation();
    this.signIn(this.signinForm.get('userData.email'), this.signinForm.get('userData.password'));
  }

  onEmailEnter(event) {
    event.preventDefault();
    event.stopPropagation();
    this.checkIfExisting(this.signinForm.get('userData.email'));
  }

  onShowPassword() {
    this.showPassword = !this.showPassword;
  }

}
